import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  ClinicCreatePayload,
  createClinic,
  getClinicById,
  updateClinic,
} from "../../api/clinic";
import { Contact } from "lucide-react";

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

// Generate 30-minute interval time options in 12-hour format
const TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, index) => {
  const hours24 = Math.floor(index / 2);
  const minutes = index % 2 === 0 ? 0 : 30;
  const period = hours24 < 12 ? "AM" : "PM";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const hourStr = hours12.toString().padStart(2, "0");
  const minuteStr = minutes === 0 ? "00" : "30";
  return `${hourStr}:${minuteStr} ${period}`;
});

const ClinicForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);
  const [formData, setFormData] = useState<ClinicCreatePayload>({
    name: "",
    address: "",
    contact: "",
    fees: "" as any,
    isOwner: true,
    openingTime: "",
    closeTime: "",
    isActive: true,
    recurringDays: [],
  });

  const [errors, setErrors] = useState<{
    name?: string;
    contact?: string;
    fees?: string;
    openingTime?: string;
    closeTime?: string;
  }>({});

  useEffect(() => {
    if (!id || fetchedRef.current) return;
    fetchedRef.current = true;
    const fetchClinic = async () => {
      setLoading(true);
      try {
        const res = await getClinicById(id as string);
        const data = res?.data?.data || res?.data || {};
        setFormData({
          name: data.name || "",
          address: data.address || "",
          contact: data.contact || null,
          fees: data.fees && data.fees > 0 ? data.fees : ("" as any),
          isOwner: true,
          openingTime: data.openingTime || null,
          closeTime: data.closeTime || null,
          isActive: data.isActive ?? true,
          recurringDays: Array.isArray(data.recurringDays)
            ? data.recurringDays
            : [],
        });
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Failed to fetch clinic");
      } finally {
        setLoading(false);
      }
    };
    fetchClinic();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, checked } = e.target as HTMLInputElement;
    if (name === "isActive") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === "contact") {
      // Only allow digits and limit to 10 characters
      const cleanValue = value.replace(/[^\d]/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, contact: cleanValue }));

      // Clear contact error when user starts typing
      if (errors.contact) {
        setErrors((prev) => ({ ...prev, contact: undefined }));
      }
    } else if (name === "fees") {
      // Allow empty string, or only positive numbers
      if (value === "") {
        setFormData((prev) => ({ ...prev, fees: "" as any }));
        // Clear fees error when field is cleared
        if (errors.fees) {
          setErrors((prev) => ({ ...prev, fees: undefined }));
        }
      } else {
        // Remove any non-digit characters except decimal point for validation
        const cleanValue = value.replace(/[^\d.]/g, "");
        const numValue = Number(cleanValue);
        // Prevent negative values - if negative, set to empty
        if (numValue < 0) {
          setFormData((prev) => ({ ...prev, fees: "" as any }));
        } else if (!isNaN(numValue)) {
          // Check if the integer part has more than 6 digits
          // Split by decimal point to get integer part
          const parts = cleanValue.split(".");
          const integerPart = parts[0] || "";
          if (integerPart.length > 6) {
            setErrors((prev) => ({ ...prev, fees: "Fees must not exceed 6 digits" }));
            // Don't update the value if it exceeds 6 digits - keep previous value
            return;
          } else {
            setFormData((prev) => ({ ...prev, fees: numValue }));
            // Clear fees error if valid
            if (errors.fees) {
              setErrors((prev) => ({ ...prev, fees: undefined }));
            }
          }
        }
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (name === "openingTime" && errors.openingTime) {
        setErrors((prev) => ({ ...prev, openingTime: undefined }));
      }
      if (name === "closeTime" && errors.closeTime) {
        setErrors((prev) => ({ ...prev, closeTime: undefined }));
      }
    }
  };

  const toggleDay = (day: string) => {
    setFormData((prev) => {
      const exists = prev.recurringDays.includes(day);
      return {
        ...prev,
        recurringDays: exists
          ? prev.recurringDays.filter((d) => d !== day)
          : [...prev.recurringDays, day],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: typeof errors = {};
    if (!formData.name.trim()) nextErrors.name = "Name is required";

    // Validate contact number
    // if (!formData.contact.trim()) {
    //   nextErrors.contact = "Contact number is required";
    // } else if (formData.contact.length !== 10) {
    //   nextErrors.contact = "Mobile number should be 10 digit";
    // }

    const feesValue = typeof formData.fees === "string" ? Number(formData.fees) : formData.fees;
    // Only validate fees when editing an existing clinic; 
    // for "Create Clinic" form, fees validation is removed as requested.
    // if (id && (!feesValue || feesValue <= 0)) {
    //   nextErrors.fees = "Fees must be greater than 0";
    // }
    // Validate fees must not exceed 6 digits
    if (feesValue && feesValue > 0) {
      const numStr = Math.floor(feesValue).toString();
      if (numStr.length > 6) {
        nextErrors.fees = "Fees must not exceed 6 digits";
      }
    }
    // if (!formData.openingTime)
    //   nextErrors.openingTime = "Opening time is required";
    // if (!formData.closeTime) nextErrors.closeTime = "Close time is required";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setLoading(true);
    try {
      // Convert fees to number before submitting and set times to null if empty
      const submitData = {
        ...formData,
        fees: typeof formData.fees === "string" ? Number(formData.fees) : formData.fees,
        contact: formData.contact || null,
        openingTime: formData.openingTime || null,
        closeTime: formData.closeTime || null,
      };
      if (id) {
        await updateClinic(id as string, submitData);
        toast.success("Clinic updated successfully");
      } else {
        await createClinic(submitData);
        toast.success("Clinic created successfully");
      }
      navigate("/clinics");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save clinic");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  {id ? "Edit Clinic" : "Create Clinic"}
                </h2>
                <button
                  className="px-4 py-1 mb-2 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>

              <form
                noValidate
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="name"
                    placeholder="Enter name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.name
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                    required
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Contact number
                  </label>
                  <input
                    name="contact"
                    type="tel"
                    placeholder="Enter 10-digit mobile number"
                    value={formData.contact || ""}
                    onChange={handleChange}
                    maxLength={10}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.contact
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                      }`}
                  />
                  {errors.contact && (
                    <p className="text-red-500 text-sm mt-1">{errors.contact}</p>
                  )}
                  {/* <div className={`text-sm mt-1 ${
                    formData.contact.length === 10 
                      ? "text-green-600 font-medium" 
                      : formData.contact.length > 0 
                        ? "text-blue-600" 
                        : "text-gray-500"
                  }`}>
                     
                  </div> */}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Address
                  </label>
                  <input
                    name="address"
                    placeholder="Enter address"
                    value={formData.address}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl`}
                  />

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Fees
                  </label>
                  <input
                    type="number"
                    name="fees"
                    value={typeof formData.fees === "string" ? formData.fees : formData.fees || ""}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    placeholder="Enter fees amount"
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.fees
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  />
                  {errors.fees && (
                    <p className="text-red-500 text-sm mt-1">{errors.fees}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Opening Time
                  </label>
                  <select
                    name="openingTime"
                    value={formData.openingTime || ""}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.openingTime
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  >
                    <option value="">Select Opening Time</option>
                    {TIME_OPTIONS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                  {errors.openingTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.openingTime}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Close Time
                  </label>
                  <select
                    name="closeTime"
                    value={formData.closeTime || ""}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.closeTime
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  >
                    <option value="">Select Close Time</option>
                    {TIME_OPTIONS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                  {errors.closeTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.closeTime}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Active
                  </label>
                  <label className="mt-1 inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-700">Is Active</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Recurring Days
                  </label>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {days.map((d) => (
                      <label
                        key={d}
                        className="inline-flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.recurringDays.includes(d)}
                          onChange={() => toggleDay(d)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <span className="text-sm text-gray-700">{d}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 rounded-xl   text-white   transition disabled:opacity-60"
                  >
                    {loading
                      ? "Saving..."
                      : id
                        ? "Update Clinic"
                        : "Save Clinic"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default ClinicForm;
